import { useMemo, useState } from 'react'
import type { CmdCommand, CmdSection } from '../api/deviceCmd'

/**
 * Coloana din dreapta terminalului: comenzile, pe sectiuni.
 *
 * Portul lui `opteemum/command_prompt/_sections.php`. Se incarca si pe Linux,
 * si pe Windows, ca sa arate la fel si sa se poarte la fel. Ce difera e doar
 * lista, care vine de la server - catalogul din baza, sau setul din cod cand
 * clientul nu si-a facut inca categoriile lui.
 *
 * O comanda are ori `cmd_id` (id din catalog, se rezolva pe server), ori
 * `command` (textul, pleaca asa cum e scris). Cele cu `has_parameters` CER
 * INTAI VALOAREA - aia e intrebarea pe care o punea x-editable in portal, si
 * ramane: nimic nu pleaca spre masina pana omul nu scrie ce vrea si nu apasa
 * Send. Daca comanda are si `placeholder`, valoarea intra acolo unde e semnul,
 * nu la coada.
 *
 * Sectiunile deschise se tin pe browser, separat pentru Linux si pentru
 * Windows: cine lucreaza numai cu servicii isi gaseste sectiunea deschisa data
 * viitoare, fara s-o mai caute.
 */

export interface CmdSectionsProps {
  sections: CmdSection[]
  note: string
  scope: 'linux' | 'windows'
  deviceId: number
  /** Fila are voie sa trimita? Fara ea butoanele se vad, dar nu pleaca nimic. */
  canSend: boolean
  onRun(command: CmdCommand, value: string | null): void
}

function storeKey(scope: string) {
  return `od-cmd-sections-${scope}`
}

function readStore(scope: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(storeKey(scope))
    return raw ? (JSON.parse(raw) as string[]) : null
  } catch {
    return null
  }
}

function writeStore(scope: string, open: string[]) {
  try {
    window.localStorage.setItem(storeKey(scope), JSON.stringify(open))
  } catch {
    /* fara localStorage ramane cum e in pagina */
  }
}

function tipOf(command: CmdCommand) {
  const text = command.command ?? ''
  return `${command.info ?? ''}${text !== '' ? `\n${text}` : ''}`.trim()
}

export function DeviceCmdSections({ sections, note, scope, deviceId, canSend, onRun }: CmdSectionsProps) {
  const [query, setQuery] = useState('')
  const [asking, setAsking] = useState<{ section: string; name: string } | null>(null)
  const [value, setValue] = useState('')
  const [missing, setMissing] = useState(false)

  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const saved = readStore(scope)
    const out: Record<string, boolean> = {}
    for (const section of sections) {
      out[section.id] = saved ? saved.includes(section.id) : !!section.open
    }
    return out
  })

  const total = useMemo(
    () => sections.reduce((sum, section) => sum + section.commands.length, 0),
    [sections],
  )

  const q = query.trim().toLowerCase()
  const hits = (command: CmdCommand) =>
    q === '' || `${command.display_name} ${tipOf(command)}`.toLowerCase().includes(q)

  const found = sections.reduce((sum, section) => sum + section.commands.filter(hits).length, 0)

  const remember = (next: Record<string, boolean>) => {
    setOpen(next)
    if (q === '') {
      writeStore(
        scope,
        Object.keys(next).filter((key) => next[key]),
      )
    }
  }

  const toggleAll = () => {
    const shouldOpen = sections.some((section) => !open[section.id])
    const next: Record<string, boolean> = {}
    for (const section of sections) next[section.id] = shouldOpen
    remember(next)
  }

  const ask = (section: string, command: CmdCommand) => {
    setAsking({ section, name: command.display_name })
    setValue('')
    setMissing(false)
  }

  const confirmValue = (command: CmdCommand) => {
    if (value.trim() === '' && command.required) {
      setMissing(true)
      return
    }
    setAsking(null)
    onRun(command, value)
  }

  return (
    <div className="cmd-sections" id={`cmdSections-${deviceId}`} data-scope={scope}>
      <div className="cmd-sections-top">
        <div className="cmd-search">
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            type="text"
            className="form-control input-sm cmd-search-input"
            placeholder={`Search in ${total} commands...`}
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQuery('')
            }}
          />
        </div>
        <button
          type="button"
          className="btn btn-sm btn-default cmd-sections-toggle"
          title="Open or close every section"
          onClick={toggleAll}
        >
          <i
            className={`fas fa-angle-double-${sections.some((s) => !open[s.id]) ? 'down' : 'up'}`}
            aria-hidden="true"
          ></i>
        </button>
      </div>

      {note !== '' && <div className="cmd-sections-note">{note}</div>}

      {sections.map((section) => {
        const shown = section.commands.filter(hits)
        if (section.commands.length === 0) return null
        const isOpen = q === '' ? !!open[section.id] : shown.length > 0
        return (
          <div
            key={section.id}
            className={`cmd-section${isOpen ? ' is-open' : ''}${q !== '' && shown.length === 0 ? ' is-hidden' : ''}`}
            data-section={section.id}
          >
            <button
              type="button"
              className="cmd-section-head"
              onClick={() => remember({ ...open, [section.id]: !open[section.id] })}
            >
              <i className={`${section.icon || 'fas fa-folder'} cmd-section-icon`} aria-hidden="true"></i>
              <span className="cmd-section-name">{section.category_name}</span>
              <span className="cmd-section-count">{q === '' ? section.commands.length : shown.length}</span>
              <i className="fas fa-chevron-down cmd-section-caret" aria-hidden="true"></i>
            </button>
            <div className="cmd-section-body">
              {section.note ? <p className="cmd-section-note">{section.note}</p> : null}
              {shown.map((command) => {
                const tone = command.tone === 'action' ? ' cmd-btn-action' : ''
                const key = `${section.id}:${command.display_name}`
                if (!command.has_parameters) {
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`init-cmd cmd-btn${tone}`}
                      data-device={deviceId}
                      data-cmd={command.cmd_id ?? 'custom'}
                      title={tipOf(command)}
                      disabled={!canSend}
                      onClick={() => onRun(command, null)}
                    >
                      {command.display_name}
                    </button>
                  )
                }
                const askOpen = asking !== null && asking.section === section.id && asking.name === command.display_name
                return (
                  <span key={key} className="cmd-btn-wrap">
                    <button
                      type="button"
                      className={`init-cmd-params cmd-btn cmd-btn-param${tone}`}
                      data-device={deviceId}
                      data-cmd={command.cmd_id ?? 'custom'}
                      title={tipOf(command)}
                      disabled={!canSend}
                      onClick={() => (askOpen ? setAsking(null) : ask(section.id, command))}
                    >
                      {command.display_name}
                      <i className="fas fa-pen cmd-btn-mark" aria-hidden="true"></i>
                    </button>
                    {askOpen && (
                      <span className="cmd-param-ask">
                        <label>{command.hint ? `Enter the ${command.hint}` : 'Enter parameters'}</label>
                        <input
                          type="text"
                          className="form-control input-sm"
                          autoFocus
                          value={value}
                          onChange={(e) => {
                            setValue(e.target.value)
                            setMissing(false)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') confirmValue(command)
                            if (e.key === 'Escape') setAsking(null)
                          }}
                        />
                        {missing && <span className="cmd-param-missing">This field is required</span>}
                        <span className="cmd-param-acts">
                          <button type="button" className="btn btn-xs btn-primary" onClick={() => confirmValue(command)}>
                            Send
                          </button>
                          <button type="button" className="btn btn-xs btn-default" onClick={() => setAsking(null)}>
                            Cancel
                          </button>
                        </span>
                      </span>
                    )}
                  </span>
                )
              })}
            </div>
          </div>
        )
      })}

      {q !== '' && found === 0 && <div className="cmd-sections-empty is-on">No command matches that.</div>}
    </div>
  )
}
